// firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import {
  initializeAuth,
  getAuth,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

// Google sign-in (popup AND redirect) loads two helpers from the authDomain:
// the OAuth page at /__/auth/handler and a hidden /__/auth/iframe that relays
// the credential back to the SDK. When authDomain is a DIFFERENT origin than
// the app, that iframe is a third party — and mobile browsers (Safari/iOS ITP,
// Android Chrome storage partitioning, third-party-cookie removal) block
// third-party storage, so the credential never makes it back and the user is
// left signed out. Serving the handler from the SAME origin as the app makes it
// first party and fixes mobile sign-in. Firebase Hosting automatically serves
// /__/auth/* on every domain connected to the site, so we can safely point
// authDomain at the current host whenever it is one of ours.
const FIRST_PARTY_AUTH_HOSTS = new Set([
  "collavue.com",
  "fluencertuber.web.app",
  "fluencertuber.firebaseapp.com",
]);

function resolveAuthDomain() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  // Prefer the app's own origin when it's a Firebase-Hosting domain. This wins
  // over REACT_APP_AUTH_DOMAIN on purpose: a stale env var pointing at the
  // firebaseapp.com domain is exactly what silently re-breaks mobile sign-in.
  if (FIRST_PARTY_AUTH_HOSTS.has(host)) return host;
  // Local dev (localhost), Firebase preview channels, or any other host: honor
  // an explicit override, else fall back to the project's Firebase domain whose
  // handler works cross-origin on desktop browsers.
  return process.env.REACT_APP_AUTH_DOMAIN || "fluencertuber.firebaseapp.com";
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API,
  authDomain: resolveAuthDomain(),
  projectId: "fluencertuber",
  storageBucket: "fluencertuber.firebasestorage.app", // ←  typical bucket domain
  messagingSenderId: "264144386202",
  appId: process.env.REACT_APP_APP_ID,
  measurementId: "G-1B12KRJT74",
};

// Avoid re‑initialising in hot‑reload
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_TEST),
  isTokenAutoRefreshEnabled: true,
});
// Analytics only on web + when it’s supported. Checked asynchronously so this
// module stays synchronous — a top-level await here held up every module that
// imports firebase.js (auth, Firestore) behind the analytics support probe.
if (typeof window !== "undefined") {
  isSupported()
    .then((ok) => {
      if (ok) getAnalytics(app);
    })
    .catch(() => {
      /* analytics unsupported (e.g. some webviews) — fine to run without */
    });
}

// Pin a durable persistence so the signed-in session survives page reloads,
// navigation, and browser restarts. Relying on the SDK default can silently
// fall back to in-memory storage in some browsers/contexts, which drops the
// user on the next load and looks like an unexpected "logout". Order matters:
// IndexedDB first, then localStorage as a fallback. The popup/redirect resolver
// is supplied here so Google sign-in keeps working with initializeAuth.
function initAuth() {
  try {
    return initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    // Already initialized (e.g. hot reload) — reuse the existing instance.
    return getAuth(app);
  }
}

export const auth = initAuth();
export const googleProvider = new GoogleAuthProvider();
export const db = getFirestore(app);
export const storage = getStorage(app);
